import {
  observeAudioForE2e,
  recordAudioPlayAttemptForE2e,
  recordAudioPlayRejectionForE2e,
  recordAudioPlayResolutionForE2e,
} from "./audioE2eProbe.client";
import { createInstrumentedAudio, type InstrumentedAudio } from "./audioInstrumentation";
import {
  claimPrimedAutoplayAudioElement,
  registerAutoplayPlaybackElement,
  unregisterAutoplayPlaybackElement,
} from "./autoplayAudioElement";
import { Sentry } from "./sentry";

/**
 * a generic interface type to provide the basic functionality for a playable audio source
 */
export type Playable = {
  play: () => Promise<void>;
  pause: () => void;
  destroy: () => void;
  getDuration: () => number;
  getPlaying: () => boolean;
  getPosition: () => number;
  getVolume: () => number;
  setPosition: (position: number) => void;
  setVolume: (volume: number) => void;
  getIdentifier: () => string;
  getOffsetForTrack: (index: number) => number;
  onEnded: ((position: number, duration: number) => void) | null;
  onSectionEnded: ((position: number, duration: number) => void) | null;
  onPlaybackError: ((error: Error) => void) | null;
};
/**
 * Creates an instance of AudioSequence and waits until metadata is
 * all loaded. this avoids the state where duration is unknown
 * @param sources An array of strings representing the audio source URLs.
 */
const LOAD_TIMEOUT_MS = 30_000;
const forceMutedForE2e = import.meta.env.VITE_AUDIO_E2E_MUTED === "1";

export async function getInitializedAudioSequence(
  sources: Array<string>,
  startingProgress?: number,
  abortSignal?: AbortSignal,
): Promise<Playable> {
  return new Promise((resolve, reject) => {
    const player = new AudioSequence(sources, startingProgress);
    let state: "pending" | "resolved" | "rejected" = "pending";
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      abortSignal?.removeEventListener("abort", handleAbort);
      player.onloadedmetadata = null;
      player.onerror = null;
    };

    const rejectWithError = (error: Error) => {
      if (state !== "pending") {
        return;
      }

      state = "rejected";
      cleanup();

      // Teardown is synchronous so callers cannot construct a fallback sequence
      // while the rejected sequence still owns media and network resources.
      player.destroy();
      reject(error);
    };

    const handleAbort = () => {
      rejectWithError(new DOMException("Audio sequence load aborted", "AbortError"));
    };

    timeoutId = setTimeout(() => {
      if (state !== "pending") {
        return;
      }

      const msg = `[AudioSequence] Metadata load timed out after ${LOAD_TIMEOUT_MS}ms. Loaded ${player.trackDurationsLoaded.size}/${sources.length} tracks. Sources: ${sources.join(", ")}`;
      console.error(msg);

      player.markUnresolvedMetadataTimeouts(LOAD_TIMEOUT_MS);
      rejectWithError(new Error(msg));
    }, LOAD_TIMEOUT_MS);

    if (abortSignal?.aborted) {
      handleAbort();
      return;
    }

    abortSignal?.addEventListener("abort", handleAbort, { once: true });

    player.onloadedmetadata = () => {
      if (state !== "pending") {
        return;
      }

      state = "resolved";
      cleanup();
      resolve(player);
    };

    player.onerror = (error: Error) => {
      rejectWithError(error);
    };
  });
}
export function generateIdentifier(sources: Array<string>) {
  return sources.join(IDENTIFIER_SEPARATOR);
}
const IDENTIFIER_SEPARATOR = "::";
/**
 * AudioSequence wraps HTMLAudioElement and provides an abstraction to
 * facilitate playing a sequence of audio tracks as if they were a single track
 */
export class AudioSequence implements Playable {
  private audioElements: Array<InstrumentedAudio> = [];
  private playbackElement: HTMLAudioElement;
  private playbackTrackIndex: number | null = null;
  private trackSources: Array<string> = [];
  private currentTrackIndex = 0;
  private isPlaying = false;
  onEnded: Playable["onEnded"] = null;
  onloadedmetadata: (() => void) | null = null;
  onSectionEnded: Playable["onSectionEnded"] = null;
  onPlaybackError: Playable["onPlaybackError"] = null;
  onerror: ((error: Error) => void) | null = null;
  trackDurationsLoaded = new Set<number>();
  readonly identifier: string;
  private ended = false;
  private startingProgress: number;
  private destroyed = false;

  /**
   * Creates an instance of AudioSequence.
   * @param sources An array of strings representing the audio source URLs.
   */
  constructor(sources: Array<string>, startingProgress = 0) {
    this.startingProgress = startingProgress;
    const sequenceId = generateIdentifier(sources);
    this.trackSources = sources.map((source) => this.encodeSource(source));
    const primedPlaybackElement = claimPrimedAutoplayAudioElement();
    const shouldContinuePrimedPlayback = Boolean(primedPlaybackElement && !primedPlaybackElement.paused);
    this.playbackElement = primedPlaybackElement ?? new Audio();
    registerAutoplayPlaybackElement(this.playbackElement, () => this.destroy());
    observeAudioForE2e(this.playbackElement);
    this.playbackElement.muted = forceMutedForE2e;
    this.playbackElement.preload = "auto";
    this.playbackElement.addEventListener("ended", () => {
      if (this.destroyed) {
        return;
      }

      this.playNextTrack();
      this.onSectionEnded?.(this.getPosition(), this.getDuration());
    });
    this.playbackElement.addEventListener("error", () => {
      if (this.destroyed) {
        return;
      }

      const error = this.createPlaybackElementError();
      Sentry.captureException(error);
      this.onerror?.(error);
    });

    this.audioElements = this.trackSources.map((uriEncodedSource, trackIndex) => {
      const audio = createInstrumentedAudio(uriEncodedSource, {
        sequenceId,
        trackIndex,
        totalTracks: sources.length,
      });
      // preload the audio for all tracks
      audio.preload = "auto";
      audio.addEventListener("loadedmetadata", () => {
        if (this.destroyed) {
          return;
        }

        this.handleLoadedMetadata(trackIndex);
      });
      audio.addEventListener("error", () => {
        if (this.destroyed) {
          return;
        }

        const mediaError = audio.error;
        const errorCode = mediaError?.code ?? 0;
        const errorMessage = mediaError?.message ?? "Unknown error";
        const errorMsg = `[AudioSequence] Audio load failed for ${uriEncodedSource}: code=${errorCode}, message=${errorMessage}`;
        const error = new Error(errorMsg);
        console.error(errorMsg, {
          errorCode,
          errorMessage,
          trackIndex,
          totalTracks: sources.length,
        });

        audio.markLoadOutcome("element_error", {
          errorCode,
          errorMessage,
        });

        Sentry.captureException(error);
        this.onerror?.(error);
      });
      return audio;
    });
    this.identifier = sequenceId;
    this.loadPlaybackTrack(0, 0, shouldContinuePrimedPlayback);
    if (shouldContinuePrimedPlayback) {
      this.isPlaying = true;
      void this.playCurrentTrack().catch((error: unknown) => {
        this.settlePlaybackError(error);
      });
    }
  }

  private encodeSource(source: string): string {
    // Failure to encode the source as a URI can cause loadedmetadata to never fire.
    // Decode first to avoid double-encoding URLs that already contain escaped bytes.
    try {
      return encodeURI(decodeURI(source));
    } catch {
      return encodeURI(source);
    }
  }

  private createPlaybackElementError(): Error {
    const mediaError = this.playbackElement.error;
    const errorCode = mediaError?.code ?? 0;
    const errorMessage = mediaError?.message ?? "Unknown error";
    const source = this.trackSources[this.currentTrackIndex] ?? "";

    return new Error(`[AudioSequence] Playback failed for ${source}: code=${errorCode}, message=${errorMessage}`);
  }

  /**
   * updates this.trackDurations and checks to see if all metadata has loaded.
   * if so, calls the onloadedmetadata callback
   * @returns A Promise that resolves when the first track starts playing.
   */
  private handleLoadedMetadata(trackIndex: number) {
    this.trackDurationsLoaded.add(trackIndex);
    const allDurationsLoaded = this.audioElements.every((_audio, index) => {
      return this.trackDurationsLoaded.has(index);
    });
    if (allDurationsLoaded) {
      const startingPosition = (this.getDuration() * this.startingProgress) / 100;
      this.setPosition(startingPosition);

      this.onloadedmetadata?.();
    }
  }

  markUnresolvedMetadataTimeouts(timeoutMs: number): void {
    this.audioElements.forEach((audio, trackIndex) => {
      if (!this.trackDurationsLoaded.has(trackIndex)) {
        audio.markLoadOutcome("metadata_timeout", { timeoutMs });
      }
    });
  }

  /**
   * Starts playing the audio sequence
   * @returns A Promise that resolves when the first track starts playing.
   */
  async play(): Promise<void> {
    if (this.audioElements.length === 0) {
      return;
    }

    if (this.ended) {
      this.setPosition(0);
      this.ended = false;
    }

    this.loadPlaybackTrack(this.currentTrackIndex, this.playbackElement.currentTime);
    this.isPlaying = true;
    recordAudioPlayAttemptForE2e(this.playbackElement);

    try {
      await this.playbackElement.play();
      recordAudioPlayResolutionForE2e(this.playbackElement);
    } catch (error) {
      recordAudioPlayRejectionForE2e(this.playbackElement, error);
      this.isPlaying = false;

      if (error instanceof DOMException && error.name === "NotAllowedError") {
        // Expected browser behavior on autoplay-blocked attempts.
        throw error;
      }

      // Unexpected error → log and bubble up
      Sentry.captureException(error);
      throw error;
    }
  }

  private async playCurrentTrack(): Promise<void> {
    if (this.audioElements.length === 0) {
      return;
    }

    this.isPlaying = true;
    recordAudioPlayAttemptForE2e(this.playbackElement);

    try {
      await this.playbackElement.play();
      recordAudioPlayResolutionForE2e(this.playbackElement);
    } catch (error) {
      recordAudioPlayRejectionForE2e(this.playbackElement, error);
      this.isPlaying = false;
      throw error;
    }
  }

  private settlePlaybackError(error: unknown): void {
    const playbackError = error instanceof Error ? error : new Error(String(error));

    this.isPlaying = false;
    Sentry.captureException(playbackError);
    this.onPlaybackError?.(playbackError);
  }

  /**
   * Pauses the currently playing audio track.
   */
  pause(): void {
    if (this.audioElements.length === 0) {
      return;
    }
    // The native element is the source of truth. In particular, iOS can begin
    // a gesture-bridged element before PlayerEngine has synchronized its
    // logical playing flag. Pausing must still stop that physical resource.
    this.playbackElement.pause();
    this.isPlaying = false;
  }

  /**
   * Stops the audio playback of a given track and resets its currentTime value to 0
   */
  private stopTrack(trackIndex: number): void {
    if (this.audioElements.length === 0) {
      return;
    }

    if (trackIndex !== this.currentTrackIndex) {
      return;
    }

    this.playbackElement.pause();
    this.playbackElement.currentTime = 0;
  }

  /**
   * Plays the next track in the sequence.
   */
  private playNextTrack(): void {
    if (this.currentTrackIndex < this.audioElements.length - 1) {
      this.currentTrackIndex++;
      this.loadPlaybackTrack(this.currentTrackIndex, 0);

      if (this.isPlaying) {
        void this.playCurrentTrack().catch((error: unknown) => {
          this.settlePlaybackError(error);
        });
      }
    } else {
      this.onEnded?.(this.getPosition(), this.getDuration());
      this.isPlaying = false;
      this.ended = true;
    }
  }

  /**
   * gets the string identifier for the Sequence
   */
  getIdentifier() {
    return this.identifier;
  }
  /**
   * Gets the current playback time in the entire sequence.
   */
  getPosition(): number {
    if (this.audioElements.length === 0) {
      return 0;
    }
    // Sum of time from previous tracks + current track's time
    const trackOffset = this.getOffsetForTrack(this.currentTrackIndex);
    const trackTime = this.playbackElement.currentTime ?? 0;
    return trackOffset + trackTime;
  }
  getOffsetForTrack(index: number) {
    return this.audioElements.slice(0, index).reduce((prev, curr) => {
      const duration = curr.duration;
      // Handle invalid durations (NaN, Infinity, or <= 0)
      if (Number.isFinite(duration) && duration > 0) {
        return prev + duration;
      }
      return prev;
    }, 0);
  }

  get currentTrack() {
    return this.playbackElement;
  }

  getVolume(): number {
    return this.playbackElement.volume ?? 1;
  }

  setVolume(volume: number) {
    const nextVolume = Math.min(Math.max(volume, 0), 1);

    this.playbackElement.volume = nextVolume;

    for (const audio of this.audioElements) {
      audio.volume = nextVolume;
    }
  }

  clampPosition(position: number) {
    if (position < 0) {
      return 0;
    }
    const duration = this.getDuration();
    // Handle invalid duration values (NaN, Infinity)
    if (!Number.isFinite(duration) || duration <= 0) {
      return 0;
    }
    if (position > duration) {
      return duration;
    }
    return position;
  }
  /**
   * Gets the current playback time in the entire sequence.
   */
  setPosition(position: number) {
    if (!Number.isFinite(position)) {
      return;
    }

    const clamped = this.clampPosition(position);
    if (!Number.isFinite(clamped)) {
      return;
    }

    if (this.audioElements.length === 0) {
      return;
    }
    //get the index of the track which contains the given position
    const nextTrackIdx = this.getBoundingTrackIndex(clamped);
    const previousTrackIndex = this.currentTrackIndex;
    //if thecurrent track and next track are different, stop the current
    if (nextTrackIdx !== previousTrackIndex) {
      this.stopTrack(previousTrackIndex);
    }
    //set next track as new current track
    this.currentTrackIndex = nextTrackIdx;
    const trackOffset = this.getOffsetForTrack(nextTrackIdx);
    const nextTime = clamped - trackOffset;
    if (!Number.isFinite(nextTime)) {
      return;
    }

    this.loadPlaybackTrack(nextTrackIdx, nextTime);

    //transition next track to playing if player is playing
    if (this.isPlaying && (nextTrackIdx !== previousTrackIndex || this.playbackElement.paused)) {
      void this.playCurrentTrack().catch((error: unknown) => {
        this.settlePlaybackError(error);
      });
    }
  }

  private loadPlaybackTrack(trackIndex: number, currentTime: number, continuePrimedPlayback = false): void {
    const source = this.trackSources[trackIndex];

    if (!source) {
      return;
    }

    if (this.playbackTrackIndex !== trackIndex) {
      if (!continuePrimedPlayback) {
        this.playbackElement.pause();
      }
      this.playbackElement.src = source;
      this.playbackElement.load();
      this.playbackTrackIndex = trackIndex;
    }

    if (Number.isFinite(currentTime)) {
      this.playbackElement.currentTime = currentTime;
    }
  }
  /**
   * finds the index of the track in this.tracks which contains the given
   * position
   */
  private getBoundingTrackIndex(position: number): number {
    if (this.audioElements.length === 0) {
      throw new Error("cannot find track: no audio elements available");
    }

    // If position is 0 or negative, return the first track
    if (position <= 0) {
      return 0;
    }

    let previousEndPosition = 0;
    for (let i = 0; i < this.audioElements.length; i++) {
      const trackDuration = this.audioElements[i].duration;

      // Skip tracks with invalid durations (NaN, Infinity, or <= 0)
      // This handles cases where metadata hasn't loaded yet
      if (!Number.isFinite(trackDuration) || trackDuration <= 0) {
        if (i === this.audioElements.length - 1) {
          return i;
        }
        continue;
      }

      const currentEndPosition = trackDuration + previousEndPosition;
      if (position < currentEndPosition) {
        return i;
      }
      previousEndPosition = currentEndPosition;
    }

    // If position is beyond all tracks, return the last valid track index
    // This handles edge cases where position might be slightly beyond duration due to rounding
    const lastIndex = this.audioElements.length - 1;
    if (lastIndex >= 0) {
      return lastIndex;
    }

    throw new Error(`could not find track for position: ${position}`);
  }
  /**
   * Gets the total duration of the entire audio sequence.
   * Note: This might be 0 if metadata hasn't loaded yet.
   */
  getDuration(): number {
    if (this.audioElements.length === 0) {
      return 0;
    }
    let totalDuration = 0;
    this.audioElements.forEach((audio) => {
      // Properly handle NaN, Infinity, and undefined/null values
      const duration = audio.duration;
      if (Number.isFinite(duration) && duration > 0) {
        totalDuration += duration;
      }
    });
    return totalDuration;
  }

  /**
   * Checks if the player is currently playing.
   */
  getPlaying(): boolean {
    return this.isPlaying;
  }

  destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    unregisterAutoplayPlaybackElement(this.playbackElement);
    this.onEnded = null;
    this.onSectionEnded = null;
    this.onloadedmetadata = null;
    this.onPlaybackError = null;
    this.onerror = null;

    this.playbackElement.pause();
    this.playbackElement.removeAttribute?.("src");
    this.playbackElement.load?.();

    for (const audio of this.audioElements) {
      audio.destroy();
      audio.pause();
      audio.removeAttribute?.("src");
      audio.load?.();
    }
    this.audioElements = [];
    this.trackSources = [];
    this.playbackTrackIndex = null;
    this.isPlaying = false;
  }
}
